import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import AddressFinder from '@eneco/components/AddressFinder/AddressFinder';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { Box, Button, Form } from '@eneco/sparky';
import { FormProvider } from 'react-hook-form';

import { toAddressFinderCustomLabels } from './address-finder-custom-labels';
import { useWarmthCheckAddressFinder } from './use-warmth-check-address-finder';
import type { WarmthCheckAddressFinderContent } from './warmth-check-address-finder-content.types';

/**
 * `WarmthCheckAddressFinder` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/forms/src/WarmCheckerForm/WarmChecker.tsx`: the shared address
 * finder, an error notification when the lookup fails, and the submit button. Its fields are read by
 * their own names, which both adapters provide; the widget gets them in its own `customLabels` shape
 * through `toAddressFinderCustomLabels`, as `AddressFinder.tsx` and `SmeAddressFinder.tsx` do.
 */
const WarmthCheckAddressFinder: FC = () => {
  const { adapter } = useCms();
  const content = useContent<WarmthCheckAddressFinderContent>();
  const { formMethods, error, handleFormSubmit, isHouseNumberSuffixRequired, setIsHouseNumberSuffixRequired } =
    useWarmthCheckAddressFinder(content);

  const { houseNumber, postalCode, houseNumberSuffix } = toAddressFinderCustomLabels(adapter, {
    houseNumberFormField: content.houseNumberFormField,
    postalCodeFormField: content.postalCodeFormField,
    houseNumberSuffixFormField: content.houseNumberSuffixFormField,
  });

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={formMethods.handleSubmit(handleFormSubmit)}>
        <Box>
          <AddressFinder
            customLabels={{ houseNumber, postalCode, houseNumberSuffix }}
            isSuffixRequired={isHouseNumberSuffixRequired}
            setIsSuffixRequired={setIsHouseNumberSuffixRequired}
          />
        </Box>

        {error && (
          <Box paddingTop="2">
            <TrackedNotificationBox
              emphasis="low"
              isAlert
              text={<RichText html={error.content || ''} />}
              title={error.title || ''}
              variant="error"
            />
          </Box>
        )}

        <Box paddingTop="6">
          <Button size="regular" type="submit">
            {content.buttonText}
          </Button>
        </Box>
      </Form>
    </FormProvider>
  );
};

export default WarmthCheckAddressFinder;
